import React from 'react';

/**
 * Render a small subset of Markdown safely.
 *
 * Shared by the talent-assessment consent pages (Big Five, DISC). The
 * consent templates are authored internally and versioned, so we don't
 * need a full parser; unknown syntax falls through as plain text.
 *
 * Handles: H1/H2/H3 headings, paragraphs, horizontal rules, bullet lists
 * (`- ` / `* `), numbered lists (`1. `, `2. `, ...), **bold** and *italic*
 * emphasis, and [link](url) anchors. List items support multi-line
 * continuations — any line in a list block that doesn't start a new item
 * is appended to the current item, matching CommonMark's "lazy
 * continuation" behaviour.
 */
const ConsentMarkdown = ({ markdown }) => {
  if (!markdown) return null;
  const blocks = markdown.split(/\n{2,}/);
  return (
    <div className="max-w-none text-gray-800">
      {blocks.map((block, idx) => {
        const trimmed = block.trim();
        if (!trimmed) return null;
        if (trimmed === '---' || trimmed === '***') {
          return <hr key={idx} className="my-6 border-gray-200" />;
        }
        if (trimmed.startsWith('### ')) {
          return <h3 key={idx} className="text-base font-semibold text-gray-900 mt-5 mb-2">{inline(trimmed.slice(4))}</h3>;
        }
        if (trimmed.startsWith('## ')) {
          return <h2 key={idx} className="text-lg font-semibold text-gray-900 mt-6 mb-2">{inline(trimmed.slice(3))}</h2>;
        }
        if (trimmed.startsWith('# ')) {
          return <h1 key={idx} className="text-xl font-bold text-gray-900 mt-2 mb-3">{inline(trimmed.slice(2))}</h1>;
        }
        const list = parseList(trimmed);
        if (list) {
          const ListTag = list.type;
          const listClass = list.type === 'ol'
            ? 'list-decimal pl-5 my-3 space-y-1.5'
            : 'list-disc pl-5 my-3 space-y-1.5';
          return (
            <ListTag key={idx} className={listClass}>
              {list.items.map((text, i) => (
                <li key={i} className="text-sm sm:text-base leading-relaxed text-gray-800 pl-1">
                  {inline(text)}
                </li>
              ))}
            </ListTag>
          );
        }
        return (
          <p key={idx} className="text-sm sm:text-base leading-relaxed text-gray-800 my-3">
            {inline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

/**
 * Detect bullet (- / *) or numbered (1. 2. ...) lists in a block, with
 * support for multi-line continuations.
 *
 * Returns null if the block isn't a list. Returns
 * `{ type: 'ul' | 'ol', items: string[] }` otherwise.
 */
const parseList = (block) => {
  const lines = block.split('\n');
  const bulletRe = /^[-*]\s+(.+)$/;
  const numberRe = /^\d+\.\s+(.+)$/;

  let listType = null;
  const items = [];

  for (const rawLine of lines) {
    const stripped = rawLine.replace(/\s+$/, '');
    const trimmed = stripped.trim();
    if (!trimmed) continue;

    const bm = trimmed.match(bulletRe);
    const nm = trimmed.match(numberRe);

    if (bm) {
      if (listType === 'ol') return null;
      listType = listType || 'ul';
      items.push(bm[1].trim());
      continue;
    }
    if (nm) {
      if (listType === 'ul') return null;
      listType = listType || 'ol';
      items.push(nm[1].trim());
      continue;
    }
    if (items.length === 0) {
      return null;
    }
    items[items.length - 1] = `${items[items.length - 1]} ${trimmed}`;
  }

  if (!listType || items.length === 0) return null;
  return { type: listType, items };
};

// Render **bold**, *italic*, and [link](url) spans within a string.
const inline = (text) => {
  const tokens = [];
  let remaining = text;
  let key = 0;
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/;
  while (remaining.length) {
    const m = re.exec(remaining);
    if (!m) {
      tokens.push(<span key={key++}>{remaining}</span>);
      break;
    }
    if (m.index > 0) tokens.push(<span key={key++}>{remaining.slice(0, m.index)}</span>);
    const tok = m[0];
    if (tok.startsWith('**')) {
      tokens.push(<strong key={key++} className="font-semibold text-gray-900">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('[')) {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm) {
        tokens.push(
          <a
            key={key++}
            href={lm[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-800 underline underline-offset-2 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
          >
            {lm[1]}
          </a>
        );
      } else {
        tokens.push(<span key={key++}>{tok}</span>);
      }
    } else {
      tokens.push(<em key={key++} className="italic">{tok.slice(1, -1)}</em>);
    }
    remaining = remaining.slice(m.index + tok.length);
  }
  return tokens;
};

export default ConsentMarkdown;
